import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import ComposioAppIcon from '@/frontend/components/Integrations/ComposioAppIcon'
import AuthConfigSelect from '@/frontend/pages/CommandCenter/Integrations/components/AuthConfigSelect'
import type { AuthConfigCopy, ComposioIntegrationRow, Pagination } from '@/frontend/pages/CommandCenter/Integrations/types'

interface Props {
  integrations: ComposioIntegrationRow[]
  pagination: Pagination
  busySlug: string | null
  disabled?: boolean
  authConfigsLoaded: boolean
  authConfigCopy: AuthConfigCopy
  onToggle: (integration: ComposioIntegrationRow) => void
  onAuthConfigChange: (integration: ComposioIntegrationRow, authConfigId: string) => void
  onPageChange: (page: number) => void
}

export default function IntegrationTable({ integrations, pagination, busySlug, disabled = false, authConfigsLoaded, authConfigCopy, onToggle, onAuthConfigChange, onPageChange }: Props) {
  const controlsDisabled = disabled || busySlug !== null

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="max-h-[calc(100vh-360px)] overflow-auto">
        <table className="w-full min-w-[1120px]">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-border bg-muted">
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Integração</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Categorias</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                {authConfigCopy.columnLabel}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Ferramentas</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase text-muted-foreground">StationZero</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {integrations.map((integration) => (
              <tr key={integration.id} className="hover:bg-accent">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <ComposioAppIcon logo={integration.logo} name={integration.name} className="h-9 w-9" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{integration.name}</p>
                      <p className="truncate font-mono text-xs text-muted-foreground">{integration.slug}</p>
                      <p className="mt-1 max-w-md truncate text-xs text-muted-foreground">
                        {integration.description || 'Sem descrição do Composio'}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex max-w-56 flex-wrap gap-1">
                    {integration.categories.length > 0 ? integration.categories.map((category) => (
                      <span key={category} className="rounded bg-secondary px-2 py-0.5 text-xs text-secondary-foreground">
                        {category}
                      </span>
                    )) : (
                      <span className="text-xs text-muted-foreground">Sem categoria</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <AuthConfigSelect
                    integration={integration}
                    copy={authConfigCopy}
                    configsLoaded={authConfigsLoaded}
                    disabled={controlsDisabled}
                    onChange={(authConfigId) => onAuthConfigChange(integration, authConfigId)}
                  />
                </td>
                <td className="px-4 py-3 text-sm text-foreground">
                  {integration.toolsCount !== null ? integration.toolsCount.toLocaleString('pt-BR') : '-'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                      integration.stationzeroEnabled
                        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {integration.stationzeroEnabled ? 'Ativa' : 'Desativada'}
                    </span>
                    <Switch
                      checked={integration.stationzeroEnabled}
                      disabled={controlsDisabled}
                      onCheckedChange={() => onToggle(integration)}
                      aria-label={`${integration.stationzeroEnabled ? 'Desativar' : 'Ativar'} ${integration.name}`}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {integrations.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  Nenhuma integração encontrada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border bg-muted px-4 py-3">
          <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.totalPages}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={controlsDisabled || pagination.page <= 1} onClick={() => onPageChange(pagination.page - 1)} aria-label="Página anterior">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={controlsDisabled || pagination.page >= pagination.totalPages} onClick={() => onPageChange(pagination.page + 1)} aria-label="Próxima página">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
